/**
 * RecordList
 *
 * Scrollable, incrementally-rendered list of RecordRows. Renders a growing
 * window (visibleCount) rather than the whole merged array, so the DOM stays
 * light even when the 1000-record live "top" is loaded. A bottom sentinel
 * (IntersectionObserver) grows the window and, once the loaded set is exhausted,
 * calls loadMore() to fetch the next Algolia page. Arrow keys move a cursor;
 * Enter opens the cursored record.
 */
import { FC, useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react';
import { Empty, Spin, Typography, theme } from 'antd';

import type { EntityRecord } from '#types';
import RecordRow from './RecordRow';

const { Text } = Typography;

const INITIAL_VISIBLE = 60;
const VISIBLE_STEP = 40;

interface RecordListProps {
    records: EntityRecord[];
    activeRecordId: string | undefined;
    onOpen: (recordId: string) => void;
    onToggleSelection: (docId: string, shiftKey: boolean) => void;
    hasMore: boolean;
    loadMore: () => void;
    loading: boolean;
    /** Changes when the active filter changes, resetting the render window + cursor. */
    resetKey: string;
    /** Placeholder shown when there are no records (varies by whether a record type is selected). */
    emptyDescription: string;
}

const RecordList: FC<RecordListProps> = ({
    records,
    activeRecordId,
    onOpen,
    onToggleSelection,
    hasMore,
    loadMore,
    loading,
    resetKey,
    emptyDescription,
}) => {
    const { token } = theme.useToken();
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
    const [cursor, setCursor] = useState(-1);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setVisibleCount(INITIAL_VISIBLE);
        setCursor(-1);
    }, [resetKey]);

    const advance = useCallback(() => {
        if (visibleCount < records.length) {
            setVisibleCount((c) => c + VISIBLE_STEP);
        } else if (hasMore) {
            loadMore();
        }
    }, [visibleCount, records.length, hasMore, loadMore]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) advance();
            },
            { root: scrollRef.current, rootMargin: '200px' },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [advance]);

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCursor((c) => Math.min(c + 1, Math.min(visibleCount, records.length) - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const record = records[cursor];
            if (record?.docId) onOpen(record.docId);
        }
    };

    if (!loading && records.length === 0) {
        return (
            <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />
            </div>
        );
    }

    return (
        <div
            ref={scrollRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            aria-label="records-list"
            style={{ flex: 1, width: '100%', minHeight: 0, overflowY: 'auto', outline: 'none' }}
        >
            {records.slice(0, visibleCount).map((record, index) => (
                <RecordRow
                    key={record.docId}
                    record={record}
                    active={record.docId === activeRecordId || index === cursor}
                    onOpen={onOpen}
                    onToggleSelection={onToggleSelection}
                />
            ))}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loading && (
                <div style={{ padding: 20, textAlign: 'center' }}>
                    <Spin size="small" />
                </div>
            )}
            {!hasMore && !loading && records.length > 0 && (
                <div style={{ padding: 20, textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                        End of results
                    </Text>
                </div>
            )}
        </div>
    );
};

export default RecordList;
